import { ResponseError } from '@backstage/errors';
import {
  DiscoveryApi,
  FetchApi,
  IdentityApi,
} from '@backstage/core-plugin-api';
import type {
  GameSession,
  LeaderboardResponse,
  SubmitScoreResult,
} from './types';

const PLUGIN_ID = 'neo4j-graph-runner';

export class GraphRunnerLeaderboardApi {
  private readonly discoveryApi: DiscoveryApi;
  private readonly fetchApi: FetchApi;
  private readonly identityApi: IdentityApi;

  constructor(options: {
    discoveryApi: DiscoveryApi;
    fetchApi: FetchApi;
    identityApi: IdentityApi;
  }) {
    this.discoveryApi = options.discoveryApi;
    this.fetchApi = options.fetchApi;
    this.identityApi = options.identityApi;
  }

  private async fetchJson(path: string, init?: RequestInit): Promise<Response> {
    const baseUrl = await this.discoveryApi.getBaseUrl(PLUGIN_ID);
    const { token } = await this.identityApi.getCredentials();
    const response = await this.fetchApi.fetch(`${baseUrl}/${path}`, {
      ...init,
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: 'application/json',
        'Content-Type': 'application/json',
        ...init?.headers,
      },
    });

    if (!response.ok) {
      throw await ResponseError.fromResponse(response);
    }

    return response;
  }

  async getLeaderboard(): Promise<LeaderboardResponse> {
    const response = await this.fetchJson('leaderboard', { method: 'GET' });
    return response.json();
  }

  async startSession(): Promise<GameSession> {
    const response = await this.fetchJson('sessions', {
      method: 'POST',
      body: JSON.stringify({}),
    });
    return response.json();
  }

  async submitScore(
    sessionId: string,
    score: number,
  ): Promise<SubmitScoreResult> {
    const response = await this.fetchJson('scores', {
      method: 'POST',
      body: JSON.stringify({
        sessionId,
        score,
      }),
    });
    return response.json();
  }

  async deleteScore(userEntityRef: string): Promise<LeaderboardResponse> {
    const response = await this.fetchJson(
      `scores?userEntityRef=${encodeURIComponent(userEntityRef)}`,
      { method: 'DELETE' },
    );
    return response.json();
  }
}
